/**
 * Compares two semantic version strings to determine if the current version is at least the minimum required version.
 *
 * @param minVersion - The minimum required version string to compare against (e.g., "17.1.0")
 * @param currentVersion - The current version string to check. Defaults to window.ContensisVersionNumber
 * @returns True if the current version is greater than or equal to the minimum required version, false otherwise
 *
 * @example
 * ```typescript
 * // Check if current version 17.102 meets minimum requirement of 17.1
 * isMinimumVersion("17.1", "17.102") // returns true
 *
 * // Check if current version 17.1 meets minimum requirement of 17.102
 * isMinimumVersion("17.102", "17.1") // returns false
 *
 * // Use default window.versionNumber as current version
 * isMinimumVersion("17.1") // returns false if window.versionNumber="17.102"
 * ```
 */
export const isMinimumVersion = (
  minVersion: string,
  currentVersion: string = window.ContensisVersionNumber || ''
) => {
  // Split version strings into arrays of numbers for comparison
  const minParts = minVersion.split('.').map(Number);
  const currentParts = currentVersion.split('.').map(Number);

  // Compare each part from left to right (major, minor, patch)
  for (let i = 0; i < Math.max(minParts.length, currentParts.length); i++) {
    const minPart = minParts[i] || 0; // Default to 0 if part doesn't exist
    const currentPart = currentParts[i] || 0; // Default to 0 if part doesn't exist

    // If current version part is greater, it meets the minimum requirement
    if (currentPart > minPart) {
      return true;
    }

    // If current version part is less, it doesn't meet the minimum requirement
    if (currentPart < minPart) {
      return false;
    }

    // If equal, continue to next part
  }

  // All parts are equal, so current version meets minimum requirement
  return true;
};
